import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, ExternalLink, Plus, Trash2, MessageCircle, Mail, Instagram, Facebook, Loader2 } from "lucide-react";
import { API, fmtDate } from "./utils";
import { T } from "@/locales/tr";
import { papicooWhatsApp } from "@/lib/whatsapp";

// Suggestions de keywords ciblés diaspora sénégalaise / Afrique francophone
const SUGGESTED_KEYWORDS = [
  "diaspora sénégalaise",
  "présidentielle sénégal 2029",
  "youtubeur sénégal",
  "influenceur sénégal",
  "photographe paris afrique",
  "art director paris dakar",
  "GP cargo dakar paris",
  "transfert argent diaspora",
  "dropshipping sénégal",
];

// Génère des deep-links de recherche pour chaque plateforme
function searchLinks(kw) {
  const k = encodeURIComponent(kw);
  return [
    { platform: "Instagram", url: `https://www.instagram.com/explore/search/keyword/?q=${k}`, color: "#E1306C", Icon: Instagram },
    { platform: "Facebook", url: `https://www.facebook.com/search/people/?q=${k}`, color: "#1877F2", Icon: Facebook },
    { platform: "X / Twitter", url: `https://twitter.com/search?q=${k}&src=typed_query&f=user`, color: "#000000", Icon: ExternalLink },
    { platform: "TikTok", url: `https://www.tiktok.com/search/user?q=${k}`, color: "#FE2C55", Icon: ExternalLink },
    { platform: "LinkedIn", url: `https://www.linkedin.com/search/results/people/?keywords=${k}`, color: "#0A66C2", Icon: ExternalLink },
    { platform: "YouTube", url: `https://www.youtube.com/results?search_query=${k}&sp=EgIQAg%253D%253D`, color: "#FF0000", Icon: ExternalLink },
  ];
}

const PLATFORMS = ["Instagram", "Facebook", "X / Twitter", "TikTok", "LinkedIn", "YouTube", "WhatsApp", "Email", "Autre"];
const STATUSES = ["à contacter", "DM envoyé", "en discussion", "client", "ignoré"];

export default function ProspectsTab({ pwd }) {
  const [keyword, setKeyword] = useState("");
  const [prospects, setProspects] = useState([]);
  const [form, setForm] = useState({ name: "", handle: "", platform: "Instagram", note: "", url: "", status: T("à contacter") });
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/prospects`, { headers: { "X-Admin-Password": pwd } });
      setProspects(data || []);
    } catch (e) { toast.error(T("Échec chargement prospects.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Nom requis."); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/prospects`, form, { headers: { "X-Admin-Password": pwd } });
      toast.success(T("Prospect ajouté."));
      setForm({ name: "", handle: "", platform: "Instagram", note: "", url: "", status: T("à contacter") });
      load();
    } catch { toast.error(T("Échec ajout.")); }
    finally { setAdding(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    try {
      await axios.delete(`${API}/admin/prospects/${id}`, { headers: { "X-Admin-Password": pwd } });
      toast.success(T("Supprimé.")); load();
    } catch { toast.error(T("Échec.")); }
  };
  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/prospects/${id}`, { status }, { headers: { "X-Admin-Password": pwd } });
      load();
    } catch { toast.error(T("Échec maj.")); }
  };

  const filtered = prospects.filter((p) =>
    !filter || p.name.toLowerCase().includes(filter.toLowerCase()) || (p.handle || "").toLowerCase().includes(filter.toLowerCase()) || (p.note || "").toLowerCase().includes(filter.toLowerCase())
  );
  const links = keyword ? searchLinks(keyword) : [];

  return (
    <div data-testid="admin-prospects-tab" className="space-y-6">
      <div>
        <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Prospects · présence sociale")}</h2>
        <p className="text-sm text-[color:var(--papicoo-muted)] mt-1">
          {T("Cherche des prospects par mot-clé sur 6 plateformes en 1 clic et garde une trace de tes outreach.")}
        </p>
      </div>

      {/* --- Recherche multi-plateformes --- */}
      <div className="papicoo-card p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-3 inline-flex items-center gap-2">
          <Search size={14} /> {T("Recherche multi-plateformes")}
        </div>
        <div className="flex gap-2 flex-col sm:flex-row mb-3">
          <input
            data-testid="prospects-keyword"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={T("mot-clé (ex: diaspora sénégalaise, dakar art, président force tranquille…)")}
            className="flex-1 px-3 py-2.5 rounded-lg border border-[color:var(--papicoo-border)] text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1 mb-4">
          {SUGGESTED_KEYWORDS.map((kw) => (
            <button key={kw} onClick={() => setKeyword(kw)} className="text-[11px] px-2.5 py-1 rounded-full bg-[color:var(--papicoo-sand-deep)] hover:bg-[color:var(--papicoo-gold)]/30 transition text-[color:var(--papicoo-ink)]">
              {kw}
            </button>
          ))}
        </div>
        {links.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {links.map((l) => (
              <a
                key={l.platform}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`prospects-link-${l.platform.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white border border-[color:var(--papicoo-border)] hover:shadow transition text-sm"
              >
                <span className="font-medium" style={{ color: l.color }}>{l.platform}</span>
                <ExternalLink size={14} className="text-[color:var(--papicoo-muted)]" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* --- Ajouter un prospect --- */}
      <div className="papicoo-card p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-3 inline-flex items-center gap-2">
          <Plus size={14} /> {T("Ajouter un prospect")}
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <input data-testid="prospect-name" placeholder={T("Nom (ex: Mariama Ndiaye)")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
          <input data-testid="prospect-handle" placeholder="@handle ou téléphone" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
          <select data-testid="prospect-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm">
            {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
          </select>
          <input data-testid="prospect-url" placeholder={T("URL profil (optionnel)")} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
          <textarea data-testid="prospect-note" placeholder={T("Note (intérêt, contexte, action prévue…)")} rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="sm:col-span-2 px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
        </div>
        <button data-testid="prospect-add" onClick={add} disabled={adding} className="px-4 py-2 rounded-full papicoo-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Ajouter
        </button>
      </div>

      {/* --- Liste --- */}
      <div className="papicoo-card p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[color:var(--papicoo-border)]">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">Pipeline · {filtered.length} prospect(s)</div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={T("Filtrer…")} className="px-2 py-1 rounded-md border border-[color:var(--papicoo-border)] text-xs" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[color:var(--papicoo-muted)] text-[11px] uppercase tracking-[0.15em]">
              <tr><th className="text-left p-3">{T("Nom")}</th><th className="text-left p-3">{T("Plateforme")}</th><th className="text-left p-3">{T("Statut")}</th><th className="text-left p-3">{T("Note")}</th><th className="text-left p-3">{T("Ajouté")}</th><th className="text-left p-3">{T("Actions")}</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-[color:var(--papicoo-muted)] italic py-6">{T("Chargement…")}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[color:var(--papicoo-muted)] italic py-6">{T("Aucun prospect. Lance une recherche ci-dessus et ajoute-en !")}</td></tr>
              ) : filtered.map((p) => (
                <tr key={p.id} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/20">
                  <td className="p-3">
                    <div className="font-medium text-[color:var(--papicoo-ink)]">{T(p.name)}</div>
                    {p.handle && <div className="text-[11px] text-[color:var(--papicoo-muted)]">{p.handle}</div>}
                    {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[11px] underline text-[color:var(--papicoo-accent)] inline-flex items-center gap-1"><ExternalLink size={10} /> {T("Ouvrir")}</a>}
                  </td>
                  <td className="p-3 text-xs">{p.platform}</td>
                  <td className="p-3">
                    <select value={p.status} onChange={(e) => updateStatus(p.id, e.target.value)} className="text-xs rounded-md border border-[color:var(--papicoo-border)] px-1.5 py-1 bg-white">
                      {STATUSES.map((s) => <option key={s} value={s}>{T(s)}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-[11px] text-[color:var(--papicoo-muted)] max-w-xs">{p.note || "—"}</td>
                  <td className="p-3 text-[11px] text-[color:var(--papicoo-muted)]">{fmtDate(p.created_at)}</td>
                  <td className="p-3">
                    <button onClick={() => del(p.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Ta présence partout (rappel) --- */}
      <div className="papicoo-card p-5 bg-gradient-to-br from-[color:var(--papicoo-gold)]/10 to-transparent">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-3">{T("Ta présence sociale actuelle (à compléter)")}</div>
        <div className="grid sm:grid-cols-3 gap-2 text-sm">
          <a href="https://www.instagram.com/papicoo75" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg bg-white border border-[color:var(--papicoo-border)] hover:shadow"><Instagram size={16} className="text-[#E1306C]" /> @papicoo75</a>
          <a href="https://www.facebook.com/share/1LZmopLS3k/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg bg-white border border-[color:var(--papicoo-border)] hover:shadow"><Facebook size={16} className="text-[#1877F2]" /> {T("Papicoo (FB)")}</a>
          <a href="https://www.tiktok.com/@www.papicoo.fr" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg bg-white border border-[color:var(--papicoo-border)] hover:shadow"><ExternalLink size={16} className="text-[#FE2C55]" /> @www.papicoo.fr</a>
          <a href="mailto:papicoo@papicoo.fr" className="flex items-center gap-2 p-3 rounded-lg bg-white border border-[color:var(--papicoo-border)] hover:shadow"><Mail size={16} className="text-[color:var(--papicoo-gold)]" /> {T("papicoo@papicoo.fr")}</a>
          <a href={papicooWhatsApp()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg bg-white border border-[color:var(--papicoo-border)] hover:shadow"><MessageCircle size={16} className="text-[#25D366]" /> {T("WhatsApp Business")}</a>
        </div>
      </div>
    </div>
  );
}
